import { PartialVibeshipConfig, VibeshipConfig } from './types';
import { validatePartialConfig } from './schema';

/**
 * Configuration migration interface
 */
export interface ConfigMigration {
  /**
   * Migration version
   */
  version: string;

  /**
   * Migration description
   */
  description: string;

  /**
   * Check if migration should be applied
   */
  shouldApply: (config: any) => boolean;

  /**
   * Apply the migration
   */
  migrate: (config: any) => PartialVibeshipConfig;

  /**
   * Validate the migration result
   */
  validate?: (config: PartialVibeshipConfig) => boolean;
}

/**
 * Available migrations
 */
const MIGRATIONS: ConfigMigration[] = [
  {
    version: '1.0.0',
    description: 'Migrate legacy devtools config to vibecode config',
    shouldApply: (config: any) => {
      return config.devtools && !config.scanPaths;
    },
    migrate: (config: any) => {
      const { devtools, ...rest } = config;
      return {
        ...rest,
        scanPaths: devtools.scanPaths || ['./docs'],
        features: {
          tasks: devtools.enableTasks !== false,
          ai: devtools.enableAI === true,
          realtime: devtools.enableRealtime !== false,
          commandPalette: devtools.enableCommandPalette !== false,
          fileEditing: devtools.enableFileEditing === true,
          markdownPreview: devtools.enableMarkdownPreview !== false,
        },
        ui: {
          theme: devtools.theme || 'system',
          position: devtools.position || 'bottom-right',
          hotkey: devtools.hotkey || 'ctrl+shift+d',
          showInProduction: devtools.showInProduction === true,
        },
      };
    },
  },
  {
    version: '1.1.0',
    description: 'Migrate old security config structure',
    shouldApply: (config: any) => {
      return config.auth && !config.security?.auth;
    },
    migrate: (config: any) => {
      const { auth, cors, rateLimit, ...rest } = config;
      return {
        ...rest,
        security: {
          ...rest.security,
          authentication: !!auth,
          auth: auth ? {
            jwtSecret: auth.jwtSecret,
            apiKey: auth.apiKey,
            session: auth.session,
            providers: auth.providers,
          } : undefined,
          cors: cors !== false,
          corsConfig: cors && typeof cors === 'object' ? cors : undefined,
          rateLimit: rateLimit !== false,
          rateLimitConfig: rateLimit && typeof rateLimit === 'object' ? rateLimit : undefined,
        },
      };
    },
  },
  {
    version: '1.2.0',
    description: 'Migrate task patterns from arrays to objects',
    shouldApply: (config: any) => {
      return config.taskPatterns && Array.isArray(config.taskPatterns);
    },
    migrate: (config: any) => {
      const patterns = config.taskPatterns as string[];
      return {
        ...config,
        taskPatterns: {
          todo: patterns.includes('TODO') || patterns.includes('todo'),
          fixme: patterns.includes('FIXME') || patterns.includes('fixme'),
          hack: patterns.includes('HACK') || patterns.includes('hack'),
          note: patterns.includes('NOTE') || patterns.includes('note'),
          warning: patterns.includes('WARNING') || patterns.includes('warning'),
          optimize: patterns.includes('OPTIMIZE') || patterns.includes('optimize'),
        },
      };
    },
  },
  {
    version: '1.3.0',
    description: 'Migrate cache config from string to object',
    shouldApply: (config: any) => {
      return typeof config.cache === 'string' || typeof config.cache === 'boolean';
    },
    migrate: (config: any) => {
      const cacheValue = config.cache;
      return {
        ...config,
        cache: {
          enabled: cacheValue !== false && cacheValue !== 'false',
          directory: typeof cacheValue === 'string' ? cacheValue : '.vibecode/cache',
          maxSize: 52428800, // 50MB
          ttl: 3600000, // 1 hour
          strategy: 'lru' as const,
        },
      };
    },
  },
  {
    version: '1.4.0',
    description: 'Migrate API config from string to object',
    shouldApply: (config: any) => {
      return typeof config.api === 'string';
    },
    migrate: (config: any) => {
      const apiValue = config.api as string;
      return {
        ...config,
        api: {
          basePath: apiValue.startsWith('/') ? apiValue : `/api/${apiValue}`,
          version: 'v1',
          documentation: true,
          timeout: 30000,
        },
      };
    },
  },
];

/**
 * Migration result
 */
export interface MigrationResult {
  /**
   * Whether any migrations were applied
   */
  migrated: boolean;

  /**
   * Applied migrations
   */
  appliedMigrations: string[];

  /**
   * Migration warnings
   */
  warnings: string[];

  /**
   * Migrated configuration
   */
  config: PartialVibeshipConfig;
}

/**
 * Apply migrations to configuration
 */
export function migrateConfig(config: any): MigrationResult {
  const result: MigrationResult = {
    migrated: false,
    appliedMigrations: [],
    warnings: [],
    config: { ...config },
  };

  // Apply migrations in order
  for (const migration of MIGRATIONS) {
    if (migration.shouldApply(result.config)) {
      try {
        const migratedConfig = migration.migrate(result.config);
        
        // Validate migration result if validator is provided
        if (migration.validate && !migration.validate(migratedConfig)) {
          result.warnings.push(
            `Migration ${migration.version} validation failed: ${migration.description}`
          );
          continue;
        }

        // Validate with schema
        const validation = validatePartialConfig(migratedConfig);
        if (!validation.success) {
          result.warnings.push(
            `Migration ${migration.version} produced invalid config: ${validation.errors?.join(', ')}`
          );
          continue;
        }

        result.config = migratedConfig;
        result.migrated = true;
        result.appliedMigrations.push(migration.version);
        
        console.log(`Applied migration ${migration.version}: ${migration.description}`);
      } catch (error) {
        result.warnings.push(
          `Migration ${migration.version} failed: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    }
  }

  return result;
}

/**
 * Check if configuration needs migration
 */
export function needsMigration(config: any): boolean {
  return MIGRATIONS.some(migration => migration.shouldApply(config));
}

/**
 * Get available migrations for a configuration
 */
export function getAvailableMigrations(config: any): ConfigMigration[] {
  return MIGRATIONS.filter(migration => migration.shouldApply(config));
}

/**
 * Create a backup of the configuration before migration
 */
export function createConfigBackup(config: any, backupPath?: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const backupName = backupPath || `vibecode.config.backup.${timestamp}.json`;
  
  const { writeFileSync } = require('fs');
  writeFileSync(backupName, JSON.stringify(config, null, 2));
  
  return backupName;
}

/**
 * Interactive migration helper
 */
export async function interactiveMigration(
  config: any,
  options: {
    prompt?: (message: string) => Promise<boolean>;
    createBackup?: boolean;
    backupPath?: string;
  } = {}
): Promise<MigrationResult> {
  const { prompt, createBackup = true, backupPath } = options;
  
  const availableMigrations = getAvailableMigrations(config);
  
  if (availableMigrations.length === 0) {
    return {
      migrated: false,
      appliedMigrations: [],
      warnings: [],
      config,
    };
  }

  // Show available migrations
  console.log('\nAvailable migrations:');
  for (const migration of availableMigrations) {
    console.log(`  ${migration.version}: ${migration.description}`);
  }

  // Ask for confirmation
  const shouldMigrate = prompt 
    ? await prompt(`Apply ${availableMigrations.length} migration(s)?`)
    : true;

  if (!shouldMigrate) {
    return {
      migrated: false,
      appliedMigrations: [],
      warnings: ['Migration cancelled by user'],
      config,
    };
  }

  // Create backup
  if (createBackup) {
    const backupFile = createConfigBackup(config, backupPath);
    console.log(`Configuration backup created: ${backupFile}`);
  }

  // Apply migrations
  return migrateConfig(config);
}

/**
 * Rollback to a previous configuration
 */
export function rollbackConfig(backupPath: string): any {
  const { readFileSync, existsSync } = require('fs');
  
  if (!existsSync(backupPath)) {
    throw new Error(`Backup file not found: ${backupPath}`);
  }

  const backupContent = readFileSync(backupPath, 'utf-8');
  return JSON.parse(backupContent);
}

/**
 * Clean up old backup files
 */
export function cleanupBackups(maxAge: number = 30 * 24 * 60 * 60 * 1000): void {
  const { readdirSync, statSync, unlinkSync } = require('fs');
  const { join } = require('path');
  
  const currentDir = process.cwd();
  const files = readdirSync(currentDir);
  const backupFiles = files.filter((file: string) => file.startsWith('vibecode.config.backup.'));
  
  const now = Date.now();
  
  for (const file of backupFiles) {
    const filePath = join(currentDir, file);
    const stats = statSync(filePath);
    
    if (now - stats.mtime.getTime() > maxAge) {
      unlinkSync(filePath);
      console.log(`Cleaned up old backup: ${file}`);
    }
  }
}